import { useState, useEffect } from 'react';
import { ShieldCheck, Lock, Undo2 } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { formatMinutes, formatEur } from '@/lib/utils';
import { useAuth } from '@/features/auth/auth.store';
import { useExtraService, useConfirmExtraService, useRevokeExtraService } from './extra-services.api';

export function ExtraServiceReviewDialog({
  serviceId,
  onClose,
}: {
  serviceId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useExtraService(serviceId);
  const confirm = useConfirmExtraService();
  const revoke = useRevokeExtraService();
  const push = useToast((s) => s.push);
  const role = useAuth((s) => s.user?.role);
  const canPrice = role === 'ADMIN' || role === 'OWNER';

  const [title, setTitle] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [netAmount, setNetAmount] = useState('');

  useEffect(() => {
    if (data) {
      setTitle(data.title);
      setReason(data.reason ?? '');
      setDescription(data.description ?? '');
      setNetAmount(data.netAmount ?? '');
    }
  }, [data]);

  const sent = (data?.approvalItems?.length ?? 0) > 0;
  const totalMinutes = data?.fieldReport.workTimes.reduce((s, w) => s + w.durationMinutes, 0) ?? 0;

  const onConfirm = async () => {
    if (!serviceId) return;
    if (!reason.trim()) {
      push('Bitte eine Begründung angeben – der Kunde muss sie nachvollziehen können.', 'error');
      return;
    }
    const amount = Number(netAmount.replace(',', '.'));
    if (!netAmount || Number.isNaN(amount) || amount < 0) {
      push('Bitte einen gültigen Nettobetrag eingeben.', 'error');
      return;
    }
    try {
      await confirm.mutateAsync({
        id: serviceId,
        input: {
          title: title.trim(),
          reason: reason.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
          netAmount: amount,
        },
      });
      push('Zusatzleistung bestätigt und bepreist.');
      onClose();
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Bestätigen fehlgeschlagen.', 'error');
    }
  };

  const onRevoke = async () => {
    if (!serviceId) return;
    try {
      await revoke.mutateAsync(serviceId);
      push('Bestätigung zurückgenommen.');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Rücknahme fehlgeschlagen.', 'error');
    }
  };

  return (
    <Dialog open={!!serviceId} onClose={onClose} title="Zusatzleistung prüfen">
      {isLoading || !data ? (
        <Spinner />
      ) : (
        <div className="space-y-5">
          <div className="text-sm">
            <p className="font-medium text-slate-900">{data.fieldReport.project.title}</p>
            <p className="text-slate-500">
              {data.fieldReport.project.customer.companyName} · {data.fieldReport.project.object.name}
            </p>
            {data.fieldReport.employee && (
              <p className="text-xs text-slate-400">gemeldet von {data.fieldReport.employee.displayName}</p>
            )}
          </div>

          {data.fieldReport.project.scopeOriginal && (
            <div className="rounded-lg bg-slate-50 p-3 text-sm">
              <span className="text-xs font-medium uppercase text-slate-500">
                Vereinbarter Leistungsumfang
              </span>
              <p className="mt-0.5 text-slate-800">{data.fieldReport.project.scopeOriginal}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
            <span>⏱ {formatMinutes(totalMinutes)} erfasst</span>
            <span>📦 {data.fieldReport.materials.length} Materialposition(en)</span>
          </div>

          {(data.fieldReport.noteText || data.fieldReport.transcript) && (
            <div className="rounded-lg bg-blue-50 p-3 text-sm text-slate-800">
              <span className="text-xs font-medium uppercase text-slate-500">Aus der Meldung</span>
              {data.fieldReport.noteText && <p className="mt-0.5">{data.fieldReport.noteText}</p>}
              {data.fieldReport.transcript && (
                <p className="mt-1 whitespace-pre-wrap text-xs text-slate-600">
                  {data.fieldReport.transcript}
                </p>
              )}
            </div>
          )}

          {sent ? (
            <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-600">
              <Lock className="h-4 w-4" />
              Dem Kunden vorgelegt – Position ist eingefroren.
              {data.approvalItems[0] && (
                <span className="ml-auto text-xs">
                  Entscheidung: {data.approvalItems[0].decision}
                </span>
              )}
            </div>
          ) : (
            <>
              <Input label="Titel für den Kunden" value={title} onChange={(e) => setTitle(e.target.value)} />
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700">Begründung *</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="Warum war das außerhalb des Leistungsumfangs?"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700">Beschreibung (optional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>

              {canPrice ? (
                <Input
                  label="Nettobetrag (€) *"
                  inputMode="decimal"
                  placeholder="z. B. 67,50"
                  value={netAmount}
                  onChange={(e) => setNetAmount(e.target.value)}
                />
              ) : (
                <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  Preis kann nur von Inhaber/Admin gesetzt werden. Sie können die Position vorbereiten
                  (Titel, Begründung, Beschreibung werden beim Bestätigen übernommen).
                </div>
              )}

              <div className="flex gap-2 border-t border-slate-200 pt-4">
                {data.confirmedInternal ? (
                  <>
                    <div className="flex flex-1 items-center gap-2 text-sm text-green-700">
                      <ShieldCheck className="h-4 w-4" />
                      Bestätigt · {formatEur(data.netAmount)}
                    </div>
                    {canPrice && (
                      <Button variant="secondary" onClick={onRevoke} loading={revoke.isPending}>
                        <Undo2 className="h-4 w-4" /> Zurücknehmen
                      </Button>
                    )}
                  </>
                ) : (
                  canPrice && (
                    <Button className="flex-1" onClick={onConfirm} loading={confirm.isPending}>
                      <ShieldCheck className="h-4 w-4" /> Bestätigen &amp; bepreisen
                    </Button>
                  )
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}
